class SignalingConnection {
  connection = null;
  interval;
  messageListeners = [];
  heartbeatTimeout = null;
  lastMessageReceived = null;
  onHeartbeatTimeout = null;

  constructor({ socketURL, onOpen, onMessage, onClose, onHeartbeatTimeout }) {
    this.socketURL = socketURL;
    this.onOpen = onOpen;
    this.messageListeners = [onMessage];
    this.onclose = onClose;
    this.onHeartbeatTimeout = onHeartbeatTimeout;
    this.connectToSocket();
  }

  sendToServer = (msg) => {
    const msgJSON = JSON.stringify(msg);

    // Check if the connection is in the OPEN state before sending
    if (this.connection && this.connection.readyState === WebSocket.OPEN) {
      this.connection.send(msgJSON);
      
      // If sending a heartbeat, start the timeout check
      if (msg.action === "heartbeat") {
        this.startHeartbeatTimeout();
      }
    } else {
      console.error("WebSocket is not in the OPEN state. Message not sent.");
    }
  };

  startHeartbeatTimeout = () => {
    // Clear any existing timeout
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
    }
    
    // Record when we sent the heartbeat
    const heartbeatSentTime = Date.now();
    
    // Set 10-second timeout
    this.heartbeatTimeout = setTimeout(() => {
      // Check if we received any message after sending the heartbeat
      if (this.lastMessageReceived === null || this.lastMessageReceived < heartbeatSentTime) {
        // No message received within 10 seconds - connection is dead
        console.warn("Heartbeat timeout: No response received within 10 seconds. Reconnecting...");
        if (this.onHeartbeatTimeout) {
          this.onHeartbeatTimeout();
        }
      }
    }, 10000); // 10 seconds
  };

  resetHeartbeatTimeout = () => {
    // Clear timeout when we receive any message
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  };

  connectToSocket = async () => {
    this.connection = new WebSocket(this.socketURL);

    if (this.connection) {
      this.connection.onopen = () => {
        // Reset message tracking on new connection
        this.lastMessageReceived = null;
        this.onOpen();
        this.interval = setInterval(() => {
          this.sendToServer({
            action: "heartbeat",
          });
        }, 30000);
      };
      this.connection.onclose = () => {
        // Clear heartbeat timeout on close
        this.resetHeartbeatTimeout();
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
        this.onclose();
      };
      this.connection.onmessage = (event) => {
        // Update last message received timestamp
        this.lastMessageReceived = Date.now();
        // Reset heartbeat timeout since we received a message
        this.resetHeartbeatTimeout();
        
        const msg = JSON.parse(event.data);
        this.messageListeners.forEach((func) => func(msg));
      };
    }
  };

  addMsgListener = (func) => {
    this.messageListeners = [...this.messageListeners, func];
    return () => {
      this.messageListeners = this.messageListeners.filter((f) => f !== func);
    };
  };

  isReady = () => {
    return this.connection
      ? this.connection?.readyState === this.connection?.OPEN
      : false;
  };

  isClosed = () => {
    return this.connection?.readyState === this.connection?.CLOSED;
  };

  close = () => {
    this.connection?.close();
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.resetHeartbeatTimeout();
  };
}

export default SignalingConnection;
